import { z } from 'zod';
import { ShipOrderClientSchema } from './economy.js';

/**
 * Fleet-view shipyard: the server prices, times and validates; the client only shows these numbers.
 * Orders themselves reuse the existing ship order rules (POST /api/systems/:id/ships).
 */
export const SHIPYARD_MAX_QUANTITY = 10;

export const SHIPYARD_CLASSES = ['corvette', 'frigate', 'cruiser', 'construction', 'scout'] as const;
export const ShipyardClassSchema = z.enum(SHIPYARD_CLASSES);
export type ShipyardClass = z.infer<typeof ShipyardClassSchema>;

const WeaponSchema = z.enum(['kinetic', 'beam', 'missile']);
const ModuleSchema = z.enum(['none', 'plating', 'shield', 'pointdef', 'ecm']);

export const ShipyardQuoteQuerySchema = z.object({
  systemId: z.string().uuid(),
  shipClass: ShipyardClassSchema,
  quantity: z.coerce.number().int().min(1).max(SHIPYARD_MAX_QUANTITY).default(1),
  weapon: WeaponSchema.optional(),
  module: ModuleSchema.optional(),
  targetFleetId: z.string().uuid().optional(),
});
export type ShipyardQuoteQuery = z.infer<typeof ShipyardQuoteQuerySchema>;

export const ShipyardOrderCommandSchema = z.object({
  systemId: z.string().uuid(),
  shipClass: ShipyardClassSchema,
  quantity: z.number().int().min(1).max(SHIPYARD_MAX_QUANTITY),
  weapon: WeaponSchema.optional(),
  module: ModuleSchema.optional(),
  targetFleetId: z.string().uuid().optional(),
  idempotencyKey: z.string().min(1).max(100),
});
export type ShipyardOrderCommand = z.infer<typeof ShipyardOrderCommandSchema>;

export const SHIPYARD_BLOCKERS = [
  'not_owned', 'locked_class', 'locked_weapon', 'locked_module', 'insufficient_resources',
  'builder_limit', 'scout_limit', 'invalid_target_fleet',
] as const;
export type ShipyardBlockerCode = typeof SHIPYARD_BLOCKERS[number];

export const ShipyardBlockerSchema = z.object({ code: z.enum(SHIPYARD_BLOCKERS), message: z.string() });
export type ShipyardBlocker = z.infer<typeof ShipyardBlockerSchema>;

export const ShipyardQuoteSchema = z.object({
  systemId: z.string().uuid(),
  shipClass: ShipyardClassSchema,
  quantity: z.number().int(),
  unit: z.object({ metal: z.number().int(), energy: z.number().int() }),
  total: z.object({ metal: z.number().int(), energy: z.number().int() }),
  /** Real milliseconds (GAME_SPEED applied) every ordered ship takes; the orders run in parallel. */
  timeMs: z.number().int(),
  /** What the ordered ships add to the hourly maintenance once they are built. */
  upkeepDelta: z.object({ energy: z.number().int(), metal: z.number().int() }),
  supply: z.object({ ships: z.number().int(), shipsAfter: z.number().int(), cap: z.number().int(), overAfter: z.number().int() }),
  /** How much of each resource is missing (0 when affordable). */
  shortfall: z.object({ metal: z.number().int(), energy: z.number().int() }),
  /** Free builder/scout slots (utility classes only). */
  slotsLeft: z.number().int().nullable(),
  blockers: z.array(ShipyardBlockerSchema),
  canOrder: z.boolean(),
});
export type ShipyardQuote = z.infer<typeof ShipyardQuoteSchema>;

export const ShipyardOverviewSchema = z.object({
  systems: z.array(z.object({ id: z.string().uuid(), name: z.string(), isHome: z.boolean() })),
  /** Classes the player may order now (cruisers need research); weapons/modules likewise. */
  unlocked: z.object({
    cruiser: z.boolean(),
    weapons: z.array(WeaponSchema),
    modules: z.array(ModuleSchema),
  }),
  orders: z.array(ShipOrderClientSchema.extend({ systemName: z.string() })),
  maxQuantity: z.number().int(),
});
export type ShipyardOverview = z.infer<typeof ShipyardOverviewSchema>;

export const ShipyardOrderResultSchema = z.object({
  orders: z.array(ShipOrderClientSchema),
});
export type ShipyardOrderResult = z.infer<typeof ShipyardOrderResultSchema>;
